import { Flex, List, Space, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { defineMessages, type IntlShape, useIntl } from 'react-intl';
import { translateBuildingFloor } from '@/features/inventorization/building-floor-options';
import { readInventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import { readInventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import { readInventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import { readBuildingNameLabel } from '@/features/inventorization/placement-options';
import { METER_LIST_ITEM_KINDS, type MeterListItem } from '@/features/meters/meter-record-list-model';
import type { CaptureSession } from '@/shared/capture/capture-session';

export interface MeterRecordSummaryData {
  dbId?: number;
  details: string;
  identity: string;
  title: string;
}

interface MeterRecordSummaryProps {
  detailsFallback?: ReactNode;
  extraLines?: ReactNode;
  summary: MeterRecordSummaryData;
  titlePrefix?: ReactNode;
  titleSuffix?: ReactNode;
}

interface MeterRecordListRowProps extends MeterRecordSummaryProps {
  actions?: ReactNode[];
  onOpen?: () => void;
  rightExtra?: ReactNode;
}

const messages = defineMessages({
  metersLocal: {
    id: 'app.inventory.meters.local',
  },
  metersTimeSpent: {
    id: 'app.inventory.meters.timeSpent',
  },
});

export function MeterRecordSummary({
  detailsFallback,
  extraLines,
  summary,
  titlePrefix,
  titleSuffix,
}: MeterRecordSummaryProps) {
  return (
    <Flex gap={2} vertical style={{ minWidth: 0, overflowWrap: 'anywhere', width: '100%' }}>
      <Flex align="center" gap="small" wrap>
        {titlePrefix}
        <Typography.Text strong>{summary.title}</Typography.Text>
        {titleSuffix}
      </Flex>
      <Typography.Text type="secondary">{summary.details || detailsFallback}</Typography.Text>
      {summary.identity ? <Typography.Text type="secondary">{summary.identity}</Typography.Text> : null}
      {extraLines}
    </Flex>
  );
}

export function MeterRecordListRow({
  actions,
  detailsFallback,
  extraLines,
  onOpen,
  rightExtra,
  summary,
  titlePrefix,
  titleSuffix,
}: MeterRecordListRowProps) {
  return (
    <List.Item
      onClick={onOpen}
      onKeyDown={event => {
        if (event.target === event.currentTarget && onOpen && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onOpen();
        }
      }}
      role={onOpen ? 'button' : undefined}
      style={{ cursor: onOpen ? 'pointer' : 'default', display: 'block' }}
      tabIndex={onOpen ? 0 : undefined}
    >
      <MeterRecordSummary
        detailsFallback={detailsFallback}
        extraLines={extraLines}
        summary={summary}
        titlePrefix={titlePrefix}
        titleSuffix={titleSuffix}
      />
      {rightExtra || actions?.length ? (
        <Flex align="center" gap="small" justify="space-between" style={{ marginTop: 8 }} wrap>
          {rightExtra}
          {actions?.length ? (
            <Space size="small" style={{ marginInlineStart: 'auto' }} wrap>
              {actions}
            </Space>
          ) : null}
        </Flex>
      ) : null}
    </List.Item>
  );
}

export function CaptureSessionMeterRecordRow({
  actions,
  detailsFallback,
  extraLines,
  onOpen,
  session,
  statusTag,
}: {
  actions?: ReactNode[];
  detailsFallback?: ReactNode;
  extraLines?: ReactNode;
  onOpen?: () => void;
  session: CaptureSession;
  statusTag?: ReactNode;
}) {
  const intl = useIntl();
  const summary = readSessionMeterSummary(session, undefined, intl);

  return (
    <MeterRecordListRow
      actions={actions}
      detailsFallback={detailsFallback}
      extraLines={extraLines}
      onOpen={onOpen}
      rightExtra={
        <Space direction="vertical" size={2}>
          {statusTag ?? <MeterRecordStatusTag status={session.status} />}
          <Typography.Text type="secondary">{formatMeterRecordTimestamp(session)}</Typography.Text>
        </Space>
      }
      summary={summary}
      titlePrefix={<MeterRecordSourceTag dbId={session.meterId} />}
    />
  );
}

export function MeterRecordSourceTag({ dbId }: { dbId?: number }) {
  const intl = useIntl();

  return dbId ? (
    <Tag color="green">DB #{dbId}</Tag>
  ) : (
    <Tag color="blue">{intl.formatMessage(messages.metersLocal)}</Tag>
  );
}

export function MeterRecordStatusTag({ status }: { status: string }) {
  const intl = useIntl();

  return <Tag color={readMeterRecordStatusColor(status)}>{intl.formatMessage(meterStatusMessage(status))}</Tag>;
}

export function readMeterListItemSummary(item: MeterListItem, intl?: IntlShape): MeterRecordSummaryData {
  if (item.kind === METER_LIST_ITEM_KINDS.cachedMeter) {
    const { data } = item.meter;

    return {
      dbId: data.id,
      details: [data.buildingName, formatOptionalFloorName(data.floorName), data.commodity, data.tenant, data.ownerName]
        .filter(Boolean)
        .join(' · '),
      identity: [formatIdentityValue('Serial', data.serial), formatIdentityValue('API', data.apiKey)]
        .filter(Boolean)
        .join(' · '),
      title: data.serial ?? data.name ?? data.apiKey ?? `Inventory meter #${data.id ?? item.meter.id}`,
    };
  }

  return readSessionMeterSummary(item.session, `Session ${item.session.id.slice(0, 14)}`, intl);
}

export function readSessionMeterSummary(
  session: CaptureSession,
  fallbackTitle = `Draft ${session.id.slice(0, 14)}`,
  intl?: IntlShape,
): MeterRecordSummaryData {
  const context = readInventorizationContext(session);
  const identity = readInventorizationIdentity(session);
  const technical = readInventorizationTechnicalDetails(session);
  const timeSpentMs = Number(session.data.timeSpentMs);
  const duration = formatDuration(timeSpentMs);
  const timeSpent =
    Number.isFinite(timeSpentMs) && timeSpentMs > 0
      ? (intl?.formatMessage(messages.metersTimeSpent, { duration }) ?? `Time ${duration}`)
      : undefined;

  return {
    dbId: session.meterId,
    details: [
      readBuildingNameLabel(context.buildingName),
      formatOptionalFloorName(context.floorName),
      identity.placement,
      context.tenantName,
      context.ownerName,
    ]
      .filter(Boolean)
      .join(' · '),
    identity: [formatIdentityValue('Serial', identity.serial), formatIdentityValue('API', identity.apiKey), timeSpent]
      .filter(Boolean)
      .join(' · '),
    title: technical.monitoredEntity.trim() || identity.serial.trim() || identity.apiKey.trim() || fallbackTitle,
  };
}

export function formatMeterRecordTimestamp(session: CaptureSession) {
  const timestamp = session.submittedAt ?? session.updatedAt;

  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
  }).format(new Date(timestamp));
}

export function formatBuildingFloorLabel(value: unknown) {
  const floor = typeof value === 'string' ? value.trim() : '';

  if (!floor) {
    return undefined;
  }

  const numericFloor = Number(floor);

  if (!Number.isInteger(numericFloor)) {
    return floor;
  }

  return translateBuildingFloor(numericFloor);
}

function formatOptionalFloorName(value: unknown) {
  return formatBuildingFloorLabel(value);
}

function formatIdentityValue(label: string, value: unknown) {
  const text = typeof value === 'string' ? value.trim() : '';

  return text ? `${label} ${text}` : undefined;
}

function meterStatusMessage(status: string) {
  return {
    id: `app.inventory.meters.status.${status.replace(/_([a-z])/g, (_, char: string) => char.toUpperCase())}`,
  };
}

function readMeterRecordStatusColor(status: string) {
  switch (status) {
    case 'queued':
    case 'syncing':
      return 'blue';
    case 'draft':
      return 'default';
    case 'sync_failed':
    case 'discarded':
      return 'red';
    case 'needs_review':
      return 'orange';
    default:
      return 'green';
  }
}

function formatDuration(durationMs: number) {
  const minutes = Math.max(1, Math.round(durationMs / 60_000));

  return `${minutes}m`;
}
